/**
 * Super Admin — Marketplace ops:
 *  /superadmin/marketplace/products   List + Feature/Remove
 *  /superadmin/marketplace/orders     List + Force refund
 *  /superadmin/marketplace/brands     Brand applications
 *  /superadmin/marketplace/reports    Reports queue
 */
import { useEffect, useState, useCallback } from "react";
import { Search, Star, Trash2, Eye } from "lucide-react";
import { apiClient } from "../../services/api";
import { toast } from "sonner";
import { SectionHeader } from "./SuperadminApp";
import {
  Card, TableShell, Th, Td, Modal, PrimaryBtn, GhostBtn, DangerBtn, StatusBadge,
  inputStyle, fieldLabel, useDebouncedValue, EmptyState, FilterPills, fmtINR, relTime,
} from "./_atoms";

// ───────────────────────────────────────────────────────────────────
// Products
// ───────────────────────────────────────────────────────────────────
export function MarketplaceProducts() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState(""); const dq = useDebouncedValue(q, 300);
  const load = useCallback(() => {
    apiClient.get("/superadmin/marketplace/products", { params: { q: dq || undefined } })
      .then((r) => setRows(r.data?.products || []));
  }, [dq]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="mk-products">
      <SectionHeader title="Marketplace Products" onRefresh={load} />
      <Card>
        <div style={{ position: "relative" }}>
          <Search size={14} style={{ position: "absolute", top: 15, left: 14, color: "var(--text-tertiary, #8A8884)" }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by title…" style={{ ...inputStyle, paddingLeft: 36 }} data-testid="mk-prod-search" />
        </div>
      </Card>
      {rows.length === 0 ? <EmptyState title="No products" /> : (
        <TableShell>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><Th>Title</Th><Th>Seller</Th><Th>Category</Th><Th>Price</Th><Th>Status</Th><Th>Featured</Th><Th></Th></tr></thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.product_id} data-testid={`mk-prod-${p.product_id}`}>
                  <Td><strong>{p.title}</strong></Td>
                  <Td style={{ fontSize: 12 }}>{p.seller_name || p.seller_id?.slice(0, 12)}</Td>
                  <Td>{p.category}</Td>
                  <Td>{fmtINR(p.price_inr)}</Td>
                  <Td><StatusBadge value={p.status} /></Td>
                  <Td>{p.is_featured ? <Star size={14} style={{ color: "var(--brand-gold, #F5C518)" }} /> : "—"}</Td>
                  <Td>
                    <div style={{ display: "flex", gap: 6 }}>
                      {!p.is_featured && (
                        <GhostBtn
                          onClick={async () => {
                            try {
                              await apiClient.post(`/superadmin/marketplace/products/${p.product_id}/feature`);
                              toast.success("Featured");
                              load();
                            } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                          }}
                          style={{ height: 30, padding: "0 10px", fontSize: 11 }}
                          data-testid={`feature-${p.product_id}`}
                        ><Star size={12} /> Feature</GhostBtn>
                      )}
                      {p.status !== "removed" && (
                        <DangerBtn
                          onClick={async () => {
                            if (!window.confirm("Remove listing?")) return;
                            await apiClient.post(`/superadmin/marketplace/products/${p.product_id}/remove`);
                            toast.success("Removed"); load();
                          }}
                          style={{ height: 30, padding: "0 10px", fontSize: 11 }}
                          data-testid={`remove-${p.product_id}`}
                        ><Trash2 size={12} /> Remove</DangerBtn>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableShell>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
// Orders
// ───────────────────────────────────────────────────────────────────
const STATUSES = [
  { value: "", label: "All" },
  { value: "confirmed", label: "Confirmed" },
  { value: "shipped", label: "Shipped" },
  { value: "delivered", label: "Delivered" },
  { value: "refunded", label: "Refunded" },
  { value: "return_admin_review", label: "Return review" },
];

export function MarketplaceOrders() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [openId, setOpenId] = useState(null);
  const load = useCallback(() => {
    apiClient.get("/superadmin/marketplace/orders", { params: { status: status || undefined, limit: 100 } })
      .then((r) => setRows(r.data?.orders || []));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="mk-orders">
      <SectionHeader title="Marketplace Orders" onRefresh={load} />
      <Card>
        <FilterPills value={status} onChange={setStatus} options={STATUSES} testid="order-status-filter" />
      </Card>
      {rows.length === 0 ? <EmptyState title="No orders match" /> : (
        <TableShell>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><Th>Order</Th><Th>Buyer</Th><Th>Items</Th><Th>Amount</Th><Th>Status</Th><Th>Created</Th><Th></Th></tr></thead>
            <tbody>
              {rows.map((o) => (
                <tr key={o.order_id} data-testid={`order-${o.order_id}`}>
                  <Td><code style={{ fontSize: 11 }}>{(o.order_id || "").slice(0, 14)}…</code></Td>
                  <Td style={{ fontSize: 12 }}>{o.buyer_name || o.buyer_id?.slice(0, 12)}</Td>
                  <Td>{(o.items || []).length}</Td>
                  <Td>{fmtINR(o.amount_inr)}</Td>
                  <Td><StatusBadge value={o.status} /></Td>
                  <Td>{relTime(o.created_at)}</Td>
                  <Td>
                    <GhostBtn onClick={() => setOpenId(o)} style={{ height: 30, padding: "0 10px", fontSize: 11 }} data-testid={`view-order-${o.order_id}`}>
                      <Eye size={12} /> View
                    </GhostBtn>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableShell>
      )}
      {openId && <OrderModal order={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}

function OrderModal({ order, onClose, onChanged }) {
  const refund = async () => {
    if (!window.confirm("Force refund this order? This cannot be undone.")) return;
    try {
      await apiClient.post(`/superadmin/marketplace/orders/${order.order_id}/force-refund`);
      toast.success("Refunded"); onChanged(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Modal open onClose={onClose} title={`Order · ${order.order_id?.slice(0, 14)}…`} maxWidth={620}>
      <div style={{ display: "grid", gap: 10 }}>
        <Card>
          <Row label="Status" value={<StatusBadge value={order.status} />} />
          <Row label="Amount" value={fmtINR(order.amount_inr)} />
          <Row label="Buyer" value={order.buyer_id} />
          <Row label="Created" value={relTime(order.created_at)} />
          <Row label="Paid at" value={order.paid_at ? new Date(order.paid_at).toLocaleString() : "—"} />
          <Row label="Razorpay" value={order.razorpay_payment_id || "—"} />
        </Card>
        <Card>
          <strong style={{ fontSize: 13 }}>Items</strong>
          {(order.items || []).map((it, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: 12 }}>
              <span>{it.title} × {it.quantity}</span>
              <span>{fmtINR(it.price_inr * (it.quantity || 1))}</span>
            </div>
          ))}
        </Card>
        {order.status !== "refunded" && (
          <DangerBtn onClick={refund} data-testid="force-refund-btn">Force refund</DangerBtn>
        )}
      </div>
    </Modal>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: 13, gap: 12 }}>
      <span style={{ color: "var(--text-tertiary, #8A8884)" }}>{label}</span>
      <span style={{ color: "var(--text-primary, #0C0C0B)", maxWidth: "60%", textAlign: "right" }}>{value}</span>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
// Brand Applications
// ───────────────────────────────────────────────────────────────────
export function MarketplaceBrands() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("pending");
  const [openId, setOpenId] = useState(null);
  const load = useCallback(() => {
    apiClient.get("/superadmin/marketplace/brand-applications", { params: { status: status || undefined } })
      .then((r) => setRows(r.data?.applications || []));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const action = async (kind, app, reason) => {
    try {
      if (kind === "approve") await apiClient.post(`/admin/brand-applications/${app.application_id}/approve`);
      if (kind === "reject") await apiClient.post(`/admin/brand-applications/${app.application_id}/reject`, { reason });
      toast.success(kind === "approve" ? "Approved" : "Rejected");
      setOpenId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="mk-brands">
      <SectionHeader title="Brand Applications" onRefresh={load} />
      <Card>
        <FilterPills
          value={status} onChange={setStatus}
          options={[{ value: "pending", label: "Pending" }, { value: "approved", label: "Approved" }, { value: "rejected", label: "Rejected" }, { value: "", label: "All" }]}
          testid="brand-filter"
        />
      </Card>
      {rows.length === 0 ? <EmptyState title="No applications" /> : (
        <TableShell>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><Th>Brand</Th><Th>GSTIN</Th><Th>Categories</Th><Th>Email</Th><Th>Status</Th><Th>Submitted</Th><Th></Th></tr></thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.application_id}>
                  <Td><strong>{a.brand_name}</strong></Td>
                  <Td style={{ fontSize: 11, fontFamily: "monospace" }}>{a.gstin}</Td>
                  <Td style={{ fontSize: 11 }}>{(a.categories || []).join(", ")}</Td>
                  <Td style={{ fontSize: 12 }}>{a.email}</Td>
                  <Td><StatusBadge value={a.status} /></Td>
                  <Td>{relTime(a.created_at)}</Td>
                  <Td>
                    <GhostBtn onClick={() => setOpenId(a)} style={{ height: 30, padding: "0 10px", fontSize: 11 }} data-testid={`brand-view-${a.application_id}`}>View</GhostBtn>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableShell>
      )}
      {openId && (
        <BrandModal app={openId} onClose={() => setOpenId(null)} onAction={action} />
      )}
    </div>
  );
}

function BrandModal({ app, onClose, onAction }) {
  const [reason, setReason] = useState("");
  return (
    <Modal open onClose={onClose} title={app.brand_name} maxWidth={620}>
      <div style={{ display: "grid", gap: 10 }}>
        <Card>
          <Row label="Status" value={<StatusBadge value={app.status} />} />
          <Row label="GSTIN" value={app.gstin} />
          <Row label="FSSAI" value={app.fssai_number || "—"} />
          <Row label="Email" value={app.email} />
          <Row label="Categories" value={(app.categories || []).join(", ")} />
          <Row label="GST cert" value={app.gst_cert_filename || "—"} />
          <Row label="FSSAI doc" value={app.fssai_doc_filename || "—"} />
          {app.rejection_reason && <Row label="Rejection reason" value={app.rejection_reason} />}
        </Card>
        {app.status === "pending" && (
          <>
            <label style={fieldLabel}>Rejection reason (≥10 chars, only required to reject)</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} style={{ ...inputStyle, height: 80, padding: "10px 14px" }} data-testid="brand-reject-reason" />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <DangerBtn
                onClick={() => {
                  if ((reason || "").length < 10) return toast.error("Rejection reason ≥10 chars");
                  onAction("reject", app, reason);
                }}
                data-testid="brand-reject"
              >Reject</DangerBtn>
              <PrimaryBtn onClick={() => onAction("approve", app)} data-testid="brand-approve">Approve</PrimaryBtn>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// ───────────────────────────────────────────────────────────────────
// Reports
// ───────────────────────────────────────────────────────────────────
export function MarketplaceReports() {
  const [rows, setRows] = useState([]);
  const [resolved, setResolved] = useState(false);
  const [openId, setOpenId] = useState(null);
  const load = useCallback(() => {
    apiClient.get("/superadmin/marketplace/reports", { params: { resolved } })
      .then((r) => setRows(r.data?.reports || []));
  }, [resolved]);
  useEffect(() => { load(); }, [load]);

  const resolve = async (rid, action, note) => {
    try {
      await apiClient.post(`/superadmin/marketplace/reports/${rid}/resolve`, { action, note });
      toast.success("Resolved");
      setOpenId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="mk-reports">
      <SectionHeader title="Marketplace Reports" onRefresh={load} />
      <Card>
        <FilterPills
          value={resolved ? "resolved" : "open"} onChange={(v) => setResolved(v === "resolved")}
          options={[{ value: "open", label: "Open" }, { value: "resolved", label: "Resolved" }]}
          testid="report-filter"
        />
      </Card>
      {rows.length === 0 ? <EmptyState title="No reports" /> : (
        <div style={{ display: "grid", gap: 10 }}>
          {rows.map((r) => (
            <Card key={r.report_id} data-testid={`report-${r.report_id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                <div>
                  <strong>{r.reason_code || "report"}</strong>
                  <div style={{ fontSize: 13, color: "var(--text-secondary, #4A4845)", marginTop: 4 }}>{r.reason_text || "—"}</div>
                  <div style={{ fontSize: 11, color: "var(--text-tertiary, #8A8884)", marginTop: 4 }}>
                    Product: {r.product_id?.slice(0, 14)}… · Reported by: {r.reporter_id?.slice(0, 12)} · {relTime(r.created_at)}
                  </div>
                </div>
                {!r.resolved ? (
                  <GhostBtn onClick={() => setOpenId(r)} style={{ height: 32, padding: "0 12px", fontSize: 12 }} data-testid={`resolve-${r.report_id}`}>Take action</GhostBtn>
                ) : <StatusBadge value="resolved" />}
              </div>
            </Card>
          ))}
        </div>
      )}
      {openId && <ReportModal report={openId} onClose={() => setOpenId(null)} onResolve={resolve} />}
    </div>
  );
}

function ReportModal({ report, onClose, onResolve }) {
  const [action, setAction] = useState("dismiss");
  const [note, setNote] = useState("");
  return (
    <Modal open onClose={onClose} title="Resolve Report">
      <div style={{ display: "grid", gap: 12 }}>
        <Card>
          <Row label="Reason" value={report.reason_code} />
          <Row label="Detail" value={report.reason_text || "—"} />
          <Row label="Product" value={report.product_id?.slice(0, 14)} />
          <Row label="Seller" value={report.seller_id?.slice(0, 14)} />
        </Card>
        <div><label style={fieldLabel}>Action</label>
          <select style={inputStyle} value={action} onChange={(e) => setAction(e.target.value)} data-testid="report-action">
            <option value="dismiss">Dismiss (no action)</option>
            <option value="remove_listing">Remove listing</option>
            <option value="ban_seller">Ban seller</option>
          </select>
        </div>
        <div><label style={fieldLabel}>Note</label><textarea style={{ ...inputStyle, height: 80, padding: "10px 14px" }} value={note} onChange={(e) => setNote(e.target.value)} /></div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <GhostBtn onClick={onClose}>Cancel</GhostBtn>
          <PrimaryBtn onClick={() => onResolve(report.report_id, action, note)} data-testid="report-confirm">Confirm</PrimaryBtn>
        </div>
      </div>
    </Modal>
  );
}
